import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AppHeader from './AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultSettings = {
  environment: 'sandbox',
  login: '',
  password: '',
  account_number: '',
  default_order_type: 'Limit',
  default_order_profile: 'default',
  max_per_trade_usd: '',
  auto_confirm: false,
  notes: '',
  updated_at: null,
};

const orderProfiles = [
  { value: 'default', label: 'Default' },
  { value: 'aggressive', label: 'Aggressive' },
  { value: 'conservative', label: 'Conservative' },
];

function numberFormatter(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  return Number(value).toFixed(2);
}

function formatRelativeTime(isoString) {
  if (!isoString) return 'Not saved yet';
  const target = new Date(isoString);
  const diff = Date.now() - target.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 2) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const sortableColumns = [
  { key: 'symbol', label: 'Symbol' },
  { key: 'quantity', label: 'Qty' },
  { key: 'average_price', label: 'Avg Price' },
  { key: 'mark_price', label: 'Mark' },
  { key: 'unrealized_pnl', label: 'Unrealized P&L' },
];

const orderColumns = [
  { key: 'symbol', label: 'Symbol' },
  { key: 'action', label: 'Side' },
  { key: 'quantity', label: 'Qty' },
  { key: 'filled_quantity', label: 'Filled' },
  { key: 'order_type', label: 'Type' },
  { key: 'limit_price', label: 'Limit' },
  { key: 'status', label: 'Status' },
  { key: 'submitted_at', label: 'Submitted' },
  { key: 'environment', label: 'Env' },
];

const cancellableStatuses = ['working', 'pending', 'received', 'submitted', 'routing', 'open'];

const TAB_STORAGE_KEY = 'tastytrade-active-tab';

const tabOptions = [
  {
    key: 'positions',
    label: 'Open Positions',
    helper: 'Live positions pulled directly from Tastytrade.',
  },
  {
    key: 'orders',
    label: 'Submitted Orders',
    helper: 'Recent trade requests routed through the scanner.',
  },
];

function formatTimestamp(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) {
    return value;
  }
  return dt.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function orderStatusBadgeClass(status = '') {
  const normalized = status.toLowerCase();
  if (normalized.includes('filled')) return 'bg-emerald-50 text-emerald-700';
  if (normalized.includes('cancel')) return 'bg-slate-100 text-slate-700';
  if (normalized.includes('reject') || normalized.includes('error')) return 'bg-rose-50 text-rose-700';
  return 'bg-indigo-50 text-indigo-700';
}

const TastytradeControlCenter = ({
  onShowScanner,
  onShowEarnings,
  onShowTastytrade,
  onShowAdmin,
  onLogout,
  isAdmin,
}) => {
  const token = useMemo(() => localStorage.getItem('token'), []);
  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const initialTab = () => {
    if (typeof window === 'undefined') return 'positions';
    return localStorage.getItem(TAB_STORAGE_KEY) || 'positions';
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [hasPassword, setHasPassword] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState({ loading: true, message: '', error: '' });
  const [validationErrors, setValidationErrors] = useState({});
  const [activeTab, setActiveTab] = useState(initialTab);

  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState('');
  const [positionsSort, setPositionsSort] = useState({ key: 'symbol', direction: 'asc' });

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [ordersActionMessage, setOrdersActionMessage] = useState('');

  const [sellModal, setSellModal] = useState({ open: false, position: null, quantity: '' });
  const [closeModal, setCloseModal] = useState({ open: false, symbol: '' });
  const [actionMessage, setActionMessage] = useState('');
  const [cancelOrderModal, setCancelOrderModal] = useState({ open: false, order: null });
  const [cancelOrderLoading, setCancelOrderLoading] = useState(false);

  const fetchSettings = useCallback(async () => {
    setSettingsStatus((prev) => ({ ...prev, loading: true, error: '', message: '' }));
    try {
      const { data } = await axios.get(`${API}/users/me/tastytrade-settings`, { headers });
      setSettings({
        environment: data.environment,
        login: data.login || '',
        password: '',
        account_number: data.account_number || '',
        default_order_type: data.default_order_type,
        default_order_profile: data.default_order_profile,
        max_per_trade_usd: data.max_per_trade_usd ?? '',
        auto_confirm: data.auto_confirm,
        notes: data.notes || '',
        updated_at: data.updated_at || null,
      });
      setHasPassword(Boolean(data.has_password));
      setSettingsStatus({ loading: false, message: '', error: '' });
      setValidationErrors({});
    } catch (error) {
      setSettingsStatus({
        loading: false,
        message: '',
        error: error.response?.data?.detail || 'Unable to load settings',
      });
    }
  }, [headers]);

  const fetchPositions = useCallback(async () => {
    setPositionsLoading(true);
    setPositionsError('');
    try {
      const { data } = await axios.get(`${API}/tastytrade/positions`, { headers });
      setPositions(data || []);
    } catch (error) {
      setPositionsError(error.response?.data?.detail || 'Unable to load positions');
    } finally {
      setPositionsLoading(false);
    }
  }, [headers]);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const { data } = await axios.get(`${API}/tastytrade/orders?limit=100`, { headers });
      setOrders(data || []);
      setOrdersLoaded(true);
    } catch (error) {
      setOrdersError(error.response?.data?.detail || 'Unable to load orders');
    } finally {
      setOrdersLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchSettings();
    fetchPositions();
  }, [fetchSettings, fetchPositions]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(TAB_STORAGE_KEY, activeTab);
    }
    if (activeTab === 'orders' && !ordersLoaded) {
      fetchOrders();
    }
  }, [activeTab, ordersLoaded, fetchOrders]);

  const handleSettingChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setSettingsStatus((prev) => ({ ...prev, message: '', error: '' }));
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateSettings = () => {
    const errors = {};
    if (!settings.login.trim()) {
      errors.login = 'Login is required.';
    }
    if (!hasPassword && !settings.password.trim()) {
      errors.password = 'Password is required for first-time setup.';
    }
    if (!settings.account_number.trim()) {
      errors.account_number = 'Account number is required.';
    }
    if (
      settings.max_per_trade_usd !== '' &&
      (Number.isNaN(Number(settings.max_per_trade_usd)) || Number(settings.max_per_trade_usd) <= 0)
    ) {
      errors.max_per_trade_usd = 'Enter a positive dollar amount or leave blank.';
    }
    return errors;
  };

  const handleEnvironmentChange = (value) => {
    if (value === settings.environment) return;
    if (value === 'production' && settings.environment === 'sandbox') {
      const confirmed = window.confirm(
        'Switch to production? All trades will route live. Continue?'
      );
      if (!confirmed) {
        return;
      }
    }
    handleSettingChange('environment', value);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
  };

  const handleSaveSettings = async (event) => {
    event.preventDefault();
    const errors = validateSettings();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    const payload = {
      environment: settings.environment,
      login: settings.login.trim() || null,
      password: settings.password ? settings.password.trim() : undefined,
      account_number: settings.account_number.trim(),
      default_order_type: settings.default_order_type,
      default_order_profile: settings.default_order_profile,
      max_per_trade_usd: settings.max_per_trade_usd === '' ? null : Number(settings.max_per_trade_usd),
      auto_confirm: settings.auto_confirm,
      notes: settings.notes || null,
    };

    setSettingsStatus({ loading: true, message: '', error: '' });
    try {
      const { data } = await axios.put(`${API}/users/me/tastytrade-settings`, payload, { headers });
      setHasPassword(Boolean(data.has_password));
      setSettings((prev) => ({
        ...prev,
        password: '',
        updated_at: data.updated_at || new Date().toISOString(),
      }));
      setSettingsStatus({ loading: false, message: '✅ Settings saved.', error: '' });
    } catch (error) {
      setSettingsStatus({
        loading: false,
        message: '',
        error: error.response?.data?.detail || 'Unable to save settings',
      });
    }
  };

  const refreshAll = useCallback(async () => {
    const tasks = [fetchSettings()];
    tasks.push(activeTab === 'orders' ? fetchOrders() : fetchPositions());
    await Promise.all(tasks);
  }, [activeTab, fetchOrders, fetchPositions, fetchSettings]);

  const requestClosePosition = (symbol) => {
    setCloseModal({ open: true, symbol });
    setActionMessage('');
  };

  const requestCancelOrder = (order) => {
    setCancelOrderModal({ open: true, order });
    setOrdersActionMessage('');
  };

  const handleCloseSubmit = async () => {
    if (!closeModal.symbol) return;
    try {
      const { data } = await axios.post(
        `${API}/tastytrade/positions/close`,
        { symbol: closeModal.symbol },
        { headers }
      );
      setActionMessage(data.message || 'Close order submitted.');
      setCloseModal({ open: false, symbol: '' });
      fetchPositions();
    } catch (error) {
      setActionMessage(error.response?.data?.detail || 'Unable to close position');
    }
  };

  const openSellModal = (position) => {
    const defaultQty = Math.max(1, Math.floor(Math.abs(position.quantity)));
    setSellModal({ open: true, position, quantity: defaultQty.toString() });
    setActionMessage('');
  };

  const handleSellSubmit = async () => {
    const qty = Number(sellModal.quantity);
    if (!qty || qty <= 0) {
      setActionMessage('Enter a valid quantity to sell.');
      return;
    }

    try {
      const { data } = await axios.post(
        `${API}/tastytrade/positions/sell`,
        { symbol: sellModal.position.symbol, quantity: qty },
        { headers }
      );
      setActionMessage(data.message || 'Sell order submitted.');
      setSellModal({ open: false, position: null, quantity: '' });
      fetchPositions();
    } catch (error) {
      setActionMessage(error.response?.data?.detail || 'Unable to sell position');
    }
  };

  const handleCancelOrderSubmit = async () => {
    if (!cancelOrderModal.order) return;
    setCancelOrderLoading(true);
    try {
      const orderId = cancelOrderModal.order.order_id;
      const { data } = await axios.post(
        `${API}/tastytrade/orders/${orderId}/cancel`,
        {},
        { headers }
      );
      setOrdersActionMessage(data.message || 'Cancel request submitted.');
      setCancelOrderModal({ open: false, order: null });
      fetchOrders();
    } catch (error) {
      setOrdersActionMessage(error.response?.data?.detail || 'Unable to cancel order');
    } finally {
      setCancelOrderLoading(false);
    }
  };

  const settingsBanner = settings.environment === 'production'
    ? 'text-rose-600 bg-rose-50 border border-rose-200'
    : 'text-emerald-600 bg-emerald-50 border border-emerald-200';

  const guardrailSummary = [
    { label: 'Account', value: settings.account_number || '—' },
    {
      label: 'Max / Trade',
      value: settings.max_per_trade_usd ? `$${numberFormatter(settings.max_per_trade_usd)}` : 'Not set',
    },
    { label: 'Auto Confirm', value: settings.auto_confirm ? 'Enabled' : 'Disabled' },
  ];

  const sortedPositions = useMemo(() => {
    const copy = [...positions];
    const { key, direction } = positionsSort;
    copy.sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      if (typeof av === 'string') {
        return direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return direction === 'asc' ? av - bv : bv - av;
    });
    return copy;
  }, [positions, positionsSort]);

  const totals = useMemo(() => positions.reduce((acc, pos) => {
    acc.quantity += Number(pos.quantity) || 0;
    acc.unrealized += Number(pos.unrealized_pnl) || 0;
    return acc;
  }, { quantity: 0, unrealized: 0 }), [positions]);

  const toggleSort = (key) => {
    setPositionsSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderValidationMessage = (field) => (
    validationErrors[field] ? <p className="mt-1 text-xs text-rose-600">{validationErrors[field]}</p> : null
  );

  const isSaveDisabled = settingsStatus.loading || Object.keys(validationErrors).length > 0;
  const isOrdersTab = activeTab === 'orders';
  const tabLoading = isOrdersTab ? ordersLoading : positionsLoading;
  const tabError = isOrdersTab ? ordersError : positionsError;
  const activeTabMeta = tabOptions.find((tab) => tab.key === activeTab) || tabOptions[0];

  const isOrderCancelable = (status = '') => {
    const normalized = (status || '').toLowerCase();
    return cancellableStatuses.some((token) => normalized.includes(token));
  };

  const headerTitle = null;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <AppHeader
        title={headerTitle}
        isAdmin={isAdmin}
        onShowScanner={onShowScanner}
        onShowEarnings={onShowEarnings}
        onShowTastytrade={onShowTastytrade}
        onShowAdmin={onShowAdmin}
        onLogout={onLogout}
      />
      <div className="flex-1 px-4 sm:px-6 py-4">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 dark:text-white">TastyTrade Settings</h2>
            </div>
            <button
              onClick={refreshAll}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-white"
              disabled={settingsStatus.loading || tabLoading}
            >
              {(settingsStatus.loading || tabLoading) ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

        <form onSubmit={handleSaveSettings} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4">
          <div className={`flex items-center justify-between rounded-md px-4 py-2 ${settingsBanner}`}>
            <div>
              <p className="text-sm font-semibold">Active Environment</p>
              <p className="text-xs">Trades & position calls use this environment and account.</p>
            </div>
            <span className="text-base font-semibold capitalize">{settings.environment}</span>
          </div>

          <div className="grid gap-3 rounded-md border border-slate-200 bg-slate-50 p-4 sm:grid-cols-4">
            {guardrailSummary.map((item) => (
              <div key={item.label}>
                <p className="text-xs uppercase tracking-wide text-slate-500">{item.label}</p>
                <p className="text-sm font-semibold text-slate-900">{item.value}</p>
              </div>
            ))}
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Credentials</p>
              <p className="text-sm font-semibold text-slate-900">
                {hasPassword ? 'Stored securely' : 'Password missing'} · {formatRelativeTime(settings.updated_at)}
              </p>
            </div>
          </div>

          {settingsStatus.error && (
            <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {settingsStatus.error}
            </div>
          )}
          {settingsStatus.message && (
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {settingsStatus.message}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-slate-700">
              Environment
              <select
                value={settings.environment}
                onChange={(e) => handleEnvironmentChange(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="sandbox">Sandbox (cert)</option>
                <option value="production">Production (live)</option>
              </select>
            </label>

            <label className="text-sm font-medium text-slate-700">
              Tastytrade Login
              <input
                type="text"
                value={settings.login}
                onChange={(e) => handleSettingChange('login', e.target.value)}
                placeholder="email@example.com"
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
              {renderValidationMessage('login')}
            </label>

            <label className="text-sm font-medium text-slate-700">
              Password {hasPassword && <span className="text-xs text-slate-500">(leave blank to keep existing)</span>}
              <input
                type="password"
                value={settings.password}
                onChange={(e) => handleSettingChange('password', e.target.value)}
                placeholder={hasPassword ? '••••••••' : 'Enter password'}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                minLength={hasPassword ? 0 : 4}
              />
              {renderValidationMessage('password')}
            </label>

            <label className="text-sm font-medium text-slate-700">
              Account Number
              <input
                type="text"
                value={settings.account_number}
                onChange={(e) => handleSettingChange('account_number', e.target.value)}
                placeholder="e.g. 5WU70837"
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
              {renderValidationMessage('account_number')}
            </label>

            <label className="text-sm font-medium text-slate-700">
              Default Order Type
              <select
                value={settings.default_order_type}
                onChange={(e) => handleSettingChange('default_order_type', e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="Limit">Limit</option>
                <option value="Market">Market</option>
              </select>
            </label>

            <label className="text-sm font-medium text-slate-700">
              Order Profile
              <select
                value={settings.default_order_profile}
                onChange={(e) => handleSettingChange('default_order_profile', e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              >
                {orderProfiles.map((profile) => (
                  <option key={profile.value} value={profile.value}>{profile.label}</option>
                ))}
              </select>
            </label>

            <label className="text-sm font-medium text-slate-700">
              Max USD Per Trade
              <input
                type="number"
                min="0"
                step="100"
                value={settings.max_per_trade_usd}
                onChange={(e) => handleSettingChange('max_per_trade_usd', e.target.value)}
                placeholder="Optional guardrail"
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
              {renderValidationMessage('max_per_trade_usd')}
            </label>

            <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={settings.auto_confirm}
                onChange={(e) => handleSettingChange('auto_confirm', e.target.checked)}
                className="h-4 w-4 text-indigo-600"
              />
              Auto-confirm trades
            </label>
          </div>

          <label className="block text-sm font-medium text-slate-700">
            Notes
            <textarea
              rows={3}
              value={settings.notes}
              onChange={(e) => handleSettingChange('notes', e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Optional memo for this routing profile"
            />
          </label>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={fetchSettings}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              disabled={settingsStatus.loading}
            >
              Reset
            </button>
            <button
              type="submit"
              className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
              disabled={isSaveDisabled}
            >
              {settingsStatus.loading ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </form>

        <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
            <div className="flex-1">
              <div className="flex flex-wrap gap-2">
                {tabOptions.map((tab) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => handleTabChange(tab.key)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition ${isActive ? 'bg-slate-900 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-sm text-slate-500">{activeTabMeta.helper}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={isOrdersTab ? fetchOrders : fetchPositions}
                className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                disabled={tabLoading}
              >
                {tabLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </header>

          {tabError && (
            <div className="mx-6 my-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {tabError}
            </div>
          )}

          {actionMessage && !isOrdersTab && (
            <div className="mx-6 my-4 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
              {actionMessage}
            </div>
          )}

          {ordersActionMessage && isOrdersTab && (
            <div className="mx-6 my-4 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
              {ordersActionMessage}
            </div>
          )}

          {activeTab === 'positions' ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {sortableColumns.map((col) => (
                      <th key={col.key} className="px-4 py-3 text-left font-semibold text-slate-600">
                        <button
                          type="button"
                          onClick={() => toggleSort(col.key)}
                          className="flex items-center gap-1 text-slate-600"
                        >
                          {col.label}
                          {positionsSort.key === col.key && (
                            <span>{positionsSort.direction === 'asc' ? '▲' : '▼'}</span>
                          )}
                        </button>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Env</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {positions.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                        {positionsLoading ? 'Loading positions…' : 'No open positions'}
                      </td>
                    </tr>
                  )}
                  {sortedPositions.map((position) => (
                    <tr key={position.position_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold text-slate-900">{position.symbol}</td>
                      <td className="px-4 py-3">{position.quantity}</td>
                      <td className="px-4 py-3">${numberFormatter(position.average_price)}</td>
                      <td className="px-4 py-3">${numberFormatter(position.mark_price)}</td>
                      <td className={`px-4 py-3 font-semibold ${(position.unrealized_pnl ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        ${numberFormatter(position.unrealized_pnl)}
                      </td>
                      <td className="px-4 py-3 capitalize">{position.environment}</td>
                      <td className="px-4 py-3 flex flex-wrap gap-2">
                        <button
                          onClick={() => requestClosePosition(position.symbol)}
                          className="rounded bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-500"
                        >
                          Close
                        </button>
                        <button
                          onClick={() => openSellModal(position)}
                          className="rounded border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Sell
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {positions.length > 0 && (
                  <tfoot className="bg-slate-50">
                    <tr>
                      <td className="px-4 py-3 font-semibold text-slate-600">Totals</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{totals.quantity}</td>
                      <td className="px-4 py-3">—</td>
                      <td className="px-4 py-3">—</td>
                      <td className={`px-4 py-3 font-semibold ${totals.unrealized >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        ${numberFormatter(totals.unrealized)}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {orderColumns.map((col) => (
                      <th key={col.key} className="px-4 py-3 text-left font-semibold text-slate-600">
                        {col.label}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orders.length === 0 && (
                    <tr>
                      <td colSpan={orderColumns.length + 1} className="px-4 py-10 text-center text-slate-500">
                        {ordersLoading ? 'Loading orders…' : 'No submitted orders yet'}
                      </td>
                    </tr>
                  )}
                  {orders.map((order) => (
                    <tr key={order.order_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold text-slate-900">{order.symbol}</td>
                      <td className="px-4 py-3 text-slate-700">{order.action || '—'}</td>
                      <td className="px-4 py-3">{order.quantity}</td>
                      <td className="px-4 py-3">{order.filled_quantity ?? '—'}</td>
                      <td className="px-4 py-3">{order.order_type || '—'}</td>
                      <td className="px-4 py-3">{order.limit_price ? `$${numberFormatter(order.limit_price)}` : '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${orderStatusBadgeClass(order.status)}`}>
                          {order.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col text-xs text-slate-600">
                          <span>{formatTimestamp(order.submitted_at)}</span>
                          <span className="text-slate-400">{order.submitted_at ? formatRelativeTime(order.submitted_at) : '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 capitalize">{order.environment}</td>
                      <td className="px-4 py-3">
                        {isOrderCancelable(order.status) ? (
                          <button
                            onClick={() => requestCancelOrder(order)}
                            className="rounded border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50"
                          >
                            Cancel
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {sellModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Sell {sellModal.position?.symbol}
            </h3>
            <p className="text-sm text-slate-500">
              Enter quantity to sell. You currently hold {sellModal.position?.quantity} shares.
            </p>
            <input
              type="number"
              min="1"
              step="1"
              value={sellModal.quantity}
              onChange={(e) => setSellModal((prev) => ({ ...prev, quantity: e.target.value }))}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSellModal({ open: false, position: null, quantity: '' })}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSellSubmit}
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
              >
                Submit Sell
              </button>
            </div>
          </div>
        </div>
      )}

      {closeModal.open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Close {closeModal.symbol}
            </h3>
            <p className="text-sm text-slate-500">
              This sends a market order to fully exit the position using your active environment.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCloseModal({ open: false, symbol: '' })}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseSubmit}
                className="rounded bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500"
              >
                Confirm Close
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelOrderModal.open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Cancel order {cancelOrderModal.order?.order_id}
            </h3>
            <p className="text-sm text-slate-500">
              This will attempt to cancel the{' '}
              <span className="font-semibold text-slate-900">{cancelOrderModal.order?.symbol}</span>{' '}
              order currently marked as "{cancelOrderModal.order?.status}".
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setCancelOrderModal({ open: false, order: null });
                  setCancelOrderLoading(false);
                }}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                disabled={cancelOrderLoading}
              >
                Never mind
              </button>
              <button
                onClick={handleCancelOrderSubmit}
                className="rounded bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-500 disabled:opacity-60"
                disabled={cancelOrderLoading}
              >
                {cancelOrderLoading ? 'Submitting…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);
};

export default TastytradeControlCenter;
